import { RouterLocation } from '@funtech-int/rival';
import classnames from 'classnames';
import React, { FC, MouseEventHandler, ReactNode, Ref, useState, useCallback, useRef } from 'react';
import { Picture, createPictureFactory, Tooltip } from '@funtech-int/yango-shared';
import { useSignal } from '@funtech-int/rival-react';
import { Episode as SeasonEpisode } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { useIntl } from 'react-intl';

import { useProfileManager } from '@/shared/hooks/useProfileManager';
import { getTruncatedText } from '@/shared/lib/text';
import { mergeRefs } from '@/shared/lib/ref';

import { useAvailabilityText } from '../../hooks/useAvailabilityText';
import { useI18n } from '../../hooks/useI18n';
import * as CardKit from '../CardKit';
import { formatDuration } from '../../lib/formatTime';

import styles from './_Episode.module.css';

const MAX_DESCRIPTION_LENGTH = 160;

const pictureFactory = createPictureFactory({
    fallback: {
        srcSet: {
            '1x': '375x234',
            '2x': '640x360', // actually 1.7x but this is maximum available size
        },
        src: '375x234',
    },
});

interface EpisodeProps {
    elementRef?: Ref<HTMLDivElement>;
    className?: string;
    location: RouterLocation<any, any>;
    episode: SeasonEpisode;
    isFilmPurchased: boolean;
    isFilmPurchasable: boolean;
    onShow?: VoidFunction;
    onClick: MouseEventHandler;
}

export const Episode: FC<EpisodeProps> = ({
    elementRef,
    episode,
    className,
    location,
    isFilmPurchased,
    isFilmPurchasable,
    onShow,
    onClick,
}) => {
    const intl = useIntl();
    const i18n = useI18n();

    const hasSubscription = useSignal(useProfileManager().hasSubscription);

    const isWatchable = Boolean(episode.viewOption?.isWatchable);
    const watchingStarted = Boolean(episode.watchProgressPosition);

    const isLocked = !episode.availabilityStatus || episode.isDisabled;

    const isEnabled = !isLocked && (!episode.isPublished || isWatchable);

    // 1. episode доступен (не disabled)
    // 2. сериал можно купить, но не куплен
    // 3. есть подписка
    const showPurchaseBadge = !isLocked && isFilmPurchasable && !isFilmPurchased && hasSubscription;

    const showProgress = episode.isPublished && isWatchable;
    const showReleaseStatusOverlay = !isLocked && !episode.isPublished;

    const title = episode.title.toString()
        ? `${episode.number}. ${episode.title.toString()}`
        : i18n('episode_card__title', episode.number);

    const description = getTruncatedText(episode.synopsis || '', MAX_DESCRIPTION_LENGTH);

    const expirationDateMs = episode.availabilityEndDate
        ? Date.parse(episode.availabilityEndDate)
        : null;

    const availabilityText = useAvailabilityText(expirationDateMs, episode.availabilityStatus);

    const showNote = Boolean(episode.editorAnnotation && isEnabled && !availabilityText);
    const showDot = !watchingStarted && isEnabled;

    const backgroundUrl = episode.cover?.toString();

    let purchaseBadge: ReactNode;
    let releaseStatusOverlay: ReactNode;
    let waitingBadge: ReactNode | undefined;
    let progress: ReactNode | undefined;

    if (showPurchaseBadge && episode.viewOption?.subscriptionBadge) {
        purchaseBadge = (
            <CardKit.PurchaseBadge
                ariaLabel={i18n('card__purchasable')}
                badge={episode.viewOption?.subscriptionBadge}
            />
        );
    }

    if (showProgress) {
        const formattedDuration = formatDuration(intl, episode.duration);

        progress = (
            <CardKit.Progress
                value={episode.watchProgressPosition ?? 0}
                maxValue={episode.duration}
                dateTime={formattedDuration}
                text={formattedDuration}
            />
        );
    }

    if (showReleaseStatusOverlay) {
        const releaseDate = episode.plannedAvailabilityDate
            ? new Date(Date.parse(episode.plannedAvailabilityDate))
            : null;

        releaseStatusOverlay = <CardKit.ReleaseDate releaseDate={releaseDate} />;
    }

    if (isLocked) {
        waitingBadge = (
            <CardKit.WaitingBadge
                text={i18n('card__waiting_unwatchable')}
                viewVariant="unwatchable"
            />
        );
    }

    const subtitleLines = availabilityText || showNote ? 3 : 4;
    const isDisabledVisually = !episode.isPublished || !isWatchable;

    const { img } = pictureFactory({
        fallbackUrl: backgroundUrl,
    });

    const showTooltip = Boolean(episode.editorAnnotation && isLocked);
    const [isHovered, setIsHovered] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);
    const handleMouseEnter = useCallback(() => setIsHovered(true), []);
    const handleMouseLeave = useCallback(() => setIsHovered(false), []);

    return (
        <CardKit.Layout
            ref={mergeRefs(elementRef, cardRef)}
            dataTid="EpisodeSnippet"
            className={classnames(
                styles.card,
                {
                    [styles.card_disabled]: isDisabledVisually,
                    [styles.card_withTooltip]: showTooltip,
                },
                className
            )}
            aria-disabled={isDisabledVisually}
            location={location}
            onShow={onShow}
            onClick={onClick}
            onMouseEnter={showTooltip ? handleMouseEnter : undefined}
            onMouseLeave={showTooltip ? handleMouseLeave : undefined}
        >
            <CardKit.ImageOverlay
                image={
                    <Picture
                        alt=""
                        className={styles.image}
                        data-tid="AdaptiveImage"
                        loading={undefined}
                        src={img.src}
                        srcSet={img.srcSet}
                    />
                }
            >
                {isLocked && <CardKit.Lock />}
                {releaseStatusOverlay}
                <div className={styles.badges}>
                    {purchaseBadge}
                    {waitingBadge}
                </div>
                {episode.isPublished && isWatchable && (
                    <CardKit.Play variant={isFilmPurchased ? 'primary' : 'plus'} />
                )}
                {progress}
            </CardKit.ImageOverlay>
            {isHovered && (
                <Tooltip
                    visible
                    direction="top"
                    alignment="middle"
                    showCloseButton={false}
                    viewVariant="secondary"
                    withOuterClick={false}
                    anchorRef={cardRef}
                >
                    {episode.editorAnnotation}
                </Tooltip>
            )}
            <CardKit.Caption
                dot={showDot}
                subtitle={isEnabled ? description : undefined}
                subtitleLines={subtitleLines}
                className={styles.caption}
                title={title}
            >
                {availabilityText && (
                    <CardKit.ExpirationLabel
                        className={styles.availabilityText}
                        variant={isEnabled ? 'warning' : 'default'}
                    >
                        {availabilityText}
                    </CardKit.ExpirationLabel>
                )}
                {showNote && <CardKit.Note lines={5}>{episode.editorAnnotation}</CardKit.Note>}
            </CardKit.Caption>
        </CardKit.Layout>
    );
};
